// src/components/property/ExcludedImagesSection.tsx
/**
 * ExcludedImagesSection Component
 *
 * Collapsible section showing excluded images with restore functionality.
 */

import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { ChevronDown, ChevronRight, RotateCcw, Loader2 } from 'lucide-react';
import type { IExcludedImagesSectionProps } from '@/@types/property';
import type { Id } from '../../../convex/_generated/dataModel';

function ExcludedThumbnail({
  storageId,
  onRestore,
}: {
  storageId: Id<'_storage'>;
  onRestore: () => void;
}) {
  const imageUrl = useQuery(api.storage.getUrl, { storageId });

  return (
    <div className="relative group rounded-lg overflow-hidden bg-muted aspect-square">
      {/* Restore button */}
      <button
        onClick={onRestore}
        className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-green-500 transition-all"
        aria-label="Restore image"
      >
        <RotateCcw className="h-4 w-4" />
      </button>

      {/* Image */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Excluded property image"
          className="w-full h-full object-cover opacity-60"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
        </div>
      )}
    </div>
  );
}

function ExcludedImagesSection({
  imageIds,
  excludedIndices,
  onRestore,
}: IExcludedImagesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (excludedIndices.length === 0) {
    return null;
  }

  const excludedImages = excludedIndices.map(index => ({
    index,
    storageId: imageIds[index],
  }));

  return (
    <div className="mt-6 border-t pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Excluded ({excludedIndices.length} images)
      </button>

      {isExpanded && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {excludedImages.map(({ index, storageId }) => (
            <ExcludedThumbnail
              key={storageId.toString()}
              storageId={storageId}
              onRestore={() => onRestore(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export { ExcludedImagesSection };
